import { useMemo } from 'react';
import { ArrowInline } from "@/lib/Icon";
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import { useActiveOrders, usePharmacyEvents } from '@/lib/api/adminHooks';
import type {
  ActiveOrdersResponse,
  ClinicalOrderSummary,
  DispenseEvent,
  PharmacyEventsResponse,
  StageBucket,
  SubmissionEvent,
} from '@/types/admin';

import { AwaitingOrdersSection } from './AwaitingOrdersSection';
import { PharmacyWarningsSection } from './PharmacyWarningsSection';
import { SyncStatusBar } from './SyncStatusBar';

const TOTAL_KEY = 'Total';

type WindowKey = '7d' | '15d' | '30d' | 'lifetime';
const WINDOWS: WindowKey[] = ['7d', '15d', '30d', 'lifetime'];
const WINDOW_DAYS: Record<WindowKey, number | null> = {
  '7d': 7,
  '15d': 15,
  '30d': 30,
  lifetime: null,
};
const TOP_N = 10;

/**
 * Clinical Ops dashboard summary. Each table shows the top {TOP_N} rows
 * by relevance (hours-in-stage for active queues, lifetime units for
 * pharmacy tables); a "View all →" link in each table header drops the
 * user into a per-table detail screen with filters + CSV export.
 *
 * Active queues use hours-in-stage extracted from each order's
 * `statusHistory` (latest transition into the current status); pharmacy
 * tables pivot a flat events feed into rolling 7/15/30-day + lifetime
 * counts. "Dispensed" = order transitioned to status='shipped';
 * "completed" is exposed separately so ops can tell "left the pharmacy"
 * from "delivered to patient".
 */
export function ClinicalOpsScreen() {
  const { data: activeData, isLoading: loadingActive, isError: activeError } =
    useActiveOrders();
  const { data: pharmEvents, isLoading: loadingPharm, isError: pharmError } =
    usePharmacyEvents();

  return (
    <div className="flex flex-col gap-24">
      <div className="flex flex-col gap-8">
        <h1 className="type-heading-xl">Clinical ops</h1>
        <p className="type-body-md text-ink-muted max-w-content-sm">
          Operational queues and pharmacy throughput at a glance. Each
          table shows the top {TOP_N}; click "View all" for filters and
          CSV export.
        </p>
      </div>

      <SyncStatusBar />

      <AwaitingOrdersSection />

      <div className=" border border-line-subtle bg-surface-subtle px-15 py-15">
        <div className="flex items-baseline justify-between gap-15 flex-wrap">
          <div className="flex flex-col gap-4">
            <h2 className="type-heading-lg">Subscription fills</h2>
            <p className="type-body-sm text-ink-muted max-w-prose">
              Upcoming shipments for multi-fill plans (quarterly with BUD-
              constrained SKUs). Fill 1 fires inline on payment; later
              fills wait here until manually triggered.
            </p>
          </div>
          <Link
            to="/admin/pharmacy/fills"
            className="type-label-md text-accent hover:underline whitespace-nowrap"
          >
            View all <ArrowInline />
          </Link>
        </div>
      </div>

      <PharmacyWarningsSection />

      <ClinicalKpis active={activeData} pharm={pharmEvents} />

      <ActiveOrdersSection
        data={activeData}
        loading={loadingActive}
        error={activeError}
      />

      <PharmacySection
        events={pharmEvents}
        loading={loadingPharm}
        error={pharmError}
      />
    </div>
  );
}

// ----- KPIs -----

function ClinicalKpis({
  active,
  pharm,
}: {
  active: ActiveOrdersResponse | undefined;
  pharm: PharmacyEventsResponse | undefined;
}) {
  // Stuck-order headline. Sums the three queues so a single number can
  // anchor the page; the sublabel breaks it down by bucket.
  const stuck = active
    ? {
        script: active.awaiting_script.orders.length,
        shipment: active.awaiting_shipment.orders.length,
        failed: active.failed_renewals.orders.length,
      }
    : null;

  // 7d vs prior-7d throughput. Computed off the events feed instead of a
  // dedicated endpoint — payload is already in scope and ~2k events
  // pivots in <1ms.
  const dispensed = pharm
    ? throughputDelta(pharm.dispense_events, 'shipped_at', (e) => e.quantity || 0)
    : null;
  const completed = pharm
    ? throughputDelta(pharm.completion_events, 'completed_at', (e) => e.quantity || 0)
    : null;
  const submitted = pharm
    ? throughputDelta(pharm.submission_events, 'order_created_at', () => 1)
    : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16">
      <KpiCard
        title="Open ops queue"
        value={stuck ? (stuck.script + stuck.shipment + stuck.failed).toLocaleString() : '—'}
        sublabel={
          stuck
            ? `${stuck.script} script · ${stuck.shipment} shipment · ${stuck.failed} failed renewals`
            : undefined
        }
        loading={!active}
      />
      <KpiCard
        title="Units dispensed · last 7 days"
        value={dispensed ? dispensed.last7d.toLocaleString() : '—'}
        sublabel={dispensed ? deltaText(dispensed) : undefined}
        loading={!pharm}
      />
      <KpiCard
        title="Units completed · last 7 days"
        value={completed ? completed.last7d.toLocaleString() : '—'}
        sublabel={completed ? deltaText(completed) : undefined}
        loading={!pharm}
      />
      <KpiCard
        title="Orders submitted · last 7 days"
        value={submitted ? submitted.last7d.toLocaleString() : '—'}
        sublabel={submitted ? deltaText(submitted) : undefined}
        loading={!pharm}
      />
    </div>
  );
}

interface ThroughputDelta {
  last7d: number;
  prior7d: number;
}

function throughputDelta<T>(
  events: T[],
  tsField: keyof T,
  weight: (e: T) => number
): ThroughputDelta {
  const now = Date.now();
  const sevenAgo = now - 7 * 86400_000;
  const fourteenAgo = now - 14 * 86400_000;
  let last7d = 0;
  let prior7d = 0;
  for (const e of events) {
    const raw = e[tsField] as unknown as string | null;
    if (!raw) continue;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) continue;
    if (t >= sevenAgo && t <= now) last7d += weight(e);
    else if (t >= fourteenAgo && t < sevenAgo) prior7d += weight(e);
  }
  return { last7d, prior7d };
}

function deltaText(d: ThroughputDelta): string {
  if (d.prior7d === 0) {
    return d.last7d > 0
      ? `${d.last7d.toLocaleString()} vs 0 prior 7d`
      : `0 prior 7d`;
  }
  const pct = ((d.last7d - d.prior7d) / d.prior7d) * 100;
  const arrow = pct > 0 ? '▲' : pct < 0 ? '▼' : '·';
  return `${arrow} ${Math.abs(pct).toFixed(0)}% vs prior 7d (${d.prior7d.toLocaleString()})`;
}

function KpiCard({
  title,
  value,
  sublabel,
  loading,
}: {
  title: string;
  value: string;
  sublabel?: string;
  loading?: boolean;
}) {
  return (
    <div className="p-16 border border-line rounded-lg bg-raised">
      <p className="type-label-sm text-ink-muted">{title}</p>
      <p className={`type-heading-lg mt-6 ${loading ? 'text-ink-muted' : ''}`}>
        {loading ? '…' : value}
      </p>
      {sublabel && <p className="type-label-sm text-ink-muted mt-4">{sublabel}</p>}
    </div>
  );
}

// ----- Active orders -----

function ActiveOrdersSection({
  data,
  loading,
  error,
}: {
  data: ReturnType<typeof useActiveOrders>['data'];
  loading: boolean;
  error: boolean;
}) {
  return (
    <section className="flex flex-col gap-12">
      <h2 className="type-heading-md">Active orders</h2>
      {error && (
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md">
          Failed to load active orders.
        </div>
      )}
      {loading && !data ? (
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md text-ink-muted">
          Loading active orders…
        </div>
      ) : null}
      {data && (
        <div className="flex flex-col gap-12">
          <StageBucketSummary
            title="Awaiting script"
            description="Orders in the provider review queue past 48h."
            bucket={data.awaiting_script}
            detailHref="/admin/clinical-ops/awaiting-script"
            stageLabel="Hours awaiting script"
          />
          <StageBucketSummary
            title="Awaiting shipment"
            description="Scripts written; queued at pharmacy past 24h."
            bucket={data.awaiting_shipment}
            detailHref="/admin/clinical-ops/awaiting-shipment"
            stageLabel="Hours awaiting shipment"
          />
          <StageBucketSummary
            title="Failed renewals"
            description="Subscription cycle orders whose latest charge attempt failed."
            bucket={data.failed_renewals}
            detailHref="/admin/clinical-ops/failed-renewals"
            stageLabel={null}
          />
        </div>
      )}
    </section>
  );
}

function StageBucketSummary({
  title,
  description,
  bucket,
  detailHref,
  stageLabel,
}: {
  title: string;
  description: string;
  bucket: StageBucket;
  detailHref: string;
  stageLabel: string | null;
}) {
  const orders = bucket.orders.slice(0, TOP_N);
  return (
    <div className="border border-line rounded-lg bg-raised overflow-hidden">
      <div className="p-16 flex flex-wrap items-baseline gap-x-12 gap-y-4 border-b border-line">
        <span className="type-heading-sm">{title}</span>
        <span className="type-label-sm text-ink-muted">
          {bucket.orders.length.toLocaleString()}
          {bucket.threshold_hours != null ? ` · ≥${bucket.threshold_hours}h` : ''}
        </span>
        <Link
          to={detailHref}
          className="ml-auto type-label-sm underline decoration-line underline-offset-4 hover:decoration-ink"
        >
          View all <ArrowInline />
        </Link>
      </div>
      <p className="px-16 pt-12 type-label-sm text-ink-muted">{description}</p>
      {orders.length === 0 ? (
        <div className="p-16 type-label-sm text-ink-muted">
          No orders in this bucket — nothing to action.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full type-label-sm">
            <thead className="bg-sunken">
              <tr className="text-left text-ink-muted">
                <Th>Customer</Th>
                <Th>Product</Th>
                <Th>Source</Th>
                {stageLabel ? <Th align="right">{stageLabel}</Th> : null}
                <Th>Created</Th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <SummaryOrderRow key={o.cp_id} order={o} showHours={!!stageLabel} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryOrderRow({
  order,
  showHours,
}: {
  order: ClinicalOrderSummary;
  showHours: boolean;
}) {
  return (
    <tr className="border-t border-line-subtle hover:bg-sunken">
      <Td>
        {order.customer_cp_id ? (
          <Link
            to={`/admin/customers/${order.customer_cp_id}`}
            className="underline decoration-line underline-offset-4 hover:decoration-ink"
          >
            {order.customer_email || order.customer_cp_id}
          </Link>
        ) : (
          order.customer_email || <span className="text-ink-muted">—</span>
        )}
      </Td>
      <Td>{order.product_label || '—'}</Td>
      <Td>
        <span className="text-ink-muted">{order.source ?? '—'}</span>
      </Td>
      {showHours && (
        <Td align="right">
          {order.hours_in_stage != null
            ? `${order.hours_in_stage.toLocaleString(undefined, {
                maximumFractionDigits: 1,
              })}h`
            : '—'}
        </Td>
      )}
      <Td>
        {order.created_at
          ? format(parseISO(order.created_at), 'MMM d, yyyy')
          : '—'}
      </Td>
    </tr>
  );
}

// ----- Pharmacy -----

function PharmacySection({
  events,
  loading,
  error,
}: {
  events: PharmacyEventsResponse | undefined;
  loading: boolean;
  error: boolean;
}) {
  if (error) {
    return (
      <section className="flex flex-col gap-12">
        <h2 className="type-heading-md">Pharmacy</h2>
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md">
          Failed to load pharmacy events.
        </div>
      </section>
    );
  }
  if (loading || !events) {
    return (
      <section className="flex flex-col gap-12">
        <h2 className="type-heading-md">Pharmacy</h2>
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md text-ink-muted">
          Loading pharmacy events…
        </div>
      </section>
    );
  }

  const pharmacies = collectPharmacies(events);

  return (
    <section className="flex flex-col gap-16">
      <h2 className="type-heading-md">Pharmacy</h2>
      <p className="type-body-sm text-ink-muted max-w-content-sm">
        Dispensed = order transitioned to <code>shipped</code>. Completed =
        order transitioned to <code>completed</code>. Submitted = order
        creation. Top {TOP_N} SKUs by lifetime volume; click View all for
        filters + export.
      </p>

      <DispenseSummary
        title="Units dispensed"
        subtitle="Per SKU × pharmacy. Counts line-item quantities at the moment the order entered status='shipped'."
        events={events.dispense_events}
        timestampField="shipped_at"
        pharmacies={pharmacies}
        detailHref="/admin/clinical-ops/dispenses"
      />

      <DispenseSummary
        title="Units completed"
        subtitle="Per SKU × pharmacy. Counts line-item quantities at the moment the order entered status='completed' (delivered)."
        events={events.completion_events}
        timestampField="completed_at"
        pharmacies={pharmacies}
        detailHref="/admin/clinical-ops/completions"
      />

      <SubmissionSummary
        events={events.submission_events}
        pharmacies={pharmacies}
        detailHref="/admin/clinical-ops/submissions"
      />
    </section>
  );
}

function collectPharmacies(events: PharmacyEventsResponse): string[] {
  const seen = new Set<string>();
  for (const e of events.dispense_events) seen.add(e.pharmacy);
  for (const e of events.completion_events) seen.add(e.pharmacy);
  for (const e of events.submission_events) seen.add(e.pharmacy);
  return [...seen].sort();
}

function inWindow(timestamp: string | null, window: WindowKey): boolean {
  if (!timestamp) return false;
  if (window === 'lifetime') return true;
  const days = WINDOW_DAYS[window];
  if (days == null) return true;
  const ts = new Date(timestamp).getTime();
  if (Number.isNaN(ts)) return false;
  return ts >= Date.now() - days * 86400_000;
}

interface SkuPivot {
  sku: string;
  total: number;
  byPharmacyWindow: Record<string, Record<WindowKey, number>>;
}

function pivotDispense(
  events: DispenseEvent[],
  timestampField: 'shipped_at' | 'completed_at',
  pharmacies: string[]
): SkuPivot[] {
  const skuMap = new Map<string, SkuPivot>();
  for (const e of events) {
    const sku = e.sku || '(unknown)';
    let pivot = skuMap.get(sku);
    if (!pivot) {
      pivot = {
        sku,
        total: 0,
        byPharmacyWindow: Object.fromEntries(
          pharmacies.map((p) => [
            p,
            { '7d': 0, '15d': 0, '30d': 0, lifetime: 0 } as Record<WindowKey, number>,
          ])
        ),
      };
      skuMap.set(sku, pivot);
    }
    const ts = e[timestampField];
    const qty = e.quantity || 0;
    for (const w of WINDOWS) {
      if (inWindow(ts, w)) {
        pivot.byPharmacyWindow[e.pharmacy][w] += qty;
        if (w === 'lifetime') pivot.total += qty;
      }
    }
  }
  return [...skuMap.values()].sort((a, b) => b.total - a.total);
}

function DispenseSummary({
  title,
  subtitle,
  events,
  timestampField,
  pharmacies,
  detailHref,
}: {
  title: string;
  subtitle: string;
  events: DispenseEvent[];
  timestampField: 'shipped_at' | 'completed_at';
  pharmacies: string[];
  detailHref: string;
}) {
  const pivots = useMemo(
    () => pivotDispense(events, timestampField, pharmacies),
    [events, timestampField, pharmacies]
  );
  const top = pivots.slice(0, TOP_N);
  const totalUnits = pivots.reduce((acc, r) => acc + r.total, 0);

  return (
    <div className="border border-line rounded-lg bg-raised overflow-hidden">
      <div className="p-16 border-b border-line flex flex-wrap items-baseline gap-x-12 gap-y-4">
        <span className="type-heading-sm">{title}</span>
        <span className="type-label-sm text-ink-muted">
          {totalUnits.toLocaleString()} lifetime · {pivots.length} SKUs
        </span>
        <Link
          to={detailHref}
          className="ml-auto type-label-sm underline decoration-line underline-offset-4 hover:decoration-ink"
        >
          View all <ArrowInline />
        </Link>
      </div>
      <p className="px-16 pt-12 type-label-sm text-ink-muted">{subtitle}</p>
      <div className="overflow-x-auto">
        <table className="w-full type-label-sm">
          <thead className="bg-sunken">
            <tr className="text-left text-ink-muted">
              <Th>SKU</Th>
              {pharmacies.map((p) =>
                WINDOWS.map((w) => (
                  <Th key={`${p}-${w}`} align="right">
                    {p} · {w}
                  </Th>
                ))
              )}
              {WINDOWS.map((w) => (
                <Th key={`total-${w}`} align="right">
                  <span className="font-medium text-ink">{TOTAL_KEY} · {w}</span>
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {top.length === 0 && (
              <tr>
                <td
                  colSpan={1 + (pharmacies.length + 1) * WINDOWS.length}
                  className="p-16 text-center text-ink-muted"
                >
                  No events in scope.
                </td>
              </tr>
            )}
            {top.map((row) => {
              const totals = WINDOWS.map((w) =>
                pharmacies.reduce((acc, p) => acc + row.byPharmacyWindow[p][w], 0)
              );
              return (
                <tr key={row.sku} className="border-t border-line-subtle hover:bg-sunken">
                  <Td>{row.sku}</Td>
                  {pharmacies.map((p) =>
                    WINDOWS.map((w) => {
                      const v = row.byPharmacyWindow[p][w];
                      return (
                        <Td key={`${row.sku}-${p}-${w}`} align="right">
                          {v > 0 ? (
                            v.toLocaleString()
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </Td>
                      );
                    })
                  )}
                  {totals.map((v, i) => (
                    <Td key={`${row.sku}-total-${WINDOWS[i]}`} align="right">
                      <span className="font-medium">
                        {v > 0 ? v.toLocaleString() : <span className="text-ink-muted font-normal">—</span>}
                      </span>
                    </Td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SubmissionSummary({
  events,
  pharmacies,
  detailHref,
}: {
  events: SubmissionEvent[];
  pharmacies: string[];
  detailHref: string;
}) {
  const pivot = useMemo(() => {
    const out: Record<string, Record<WindowKey, number>> = Object.fromEntries(
      pharmacies.map((p) => [
        p,
        { '7d': 0, '15d': 0, '30d': 0, lifetime: 0 } as Record<WindowKey, number>,
      ])
    );
    for (const e of events) {
      for (const w of WINDOWS) {
        if (inWindow(e.order_created_at, w)) out[e.pharmacy][w] += 1;
      }
    }
    return out;
  }, [events, pharmacies]);

  return (
    <div className="border border-line rounded-lg bg-raised overflow-hidden">
      <div className="p-16 border-b border-line flex flex-wrap items-baseline gap-x-12 gap-y-4">
        <span className="type-heading-sm">Orders submitted</span>
        <span className="type-label-sm text-ink-muted">
          {events.length.toLocaleString()} lifetime
        </span>
        <Link
          to={detailHref}
          className="ml-auto type-label-sm underline decoration-line underline-offset-4 hover:decoration-ink"
        >
          View all <ArrowInline />
        </Link>
      </div>
      <p className="px-16 pt-12 type-label-sm text-ink-muted">
        Per pharmacy. Counts each order once per pharmacy assigned on its
        line items, keyed off order creation time.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full type-label-sm">
          <thead className="bg-sunken">
            <tr className="text-left text-ink-muted">
              <Th>Pharmacy</Th>
              {WINDOWS.map((w) => (
                <Th key={w} align="right">
                  {w}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pharmacies.map((p) => (
              <tr key={p} className="border-t border-line-subtle hover:bg-sunken">
                <Td>{p}</Td>
                {WINDOWS.map((w) => {
                  const v = pivot[p][w];
                  return (
                    <Td key={`${p}-${w}`} align="right">
                      {v > 0 ? (
                        v.toLocaleString()
                      ) : (
                        <span className="text-ink-muted">—</span>
                      )}
                    </Td>
                  );
                })}
              </tr>
            ))}
            <tr className="border-t-2 border-line bg-sunken">
              <Td>
                <span className="font-medium">{TOTAL_KEY}</span>
              </Td>
              {WINDOWS.map((w) => {
                const v = pharmacies.reduce((acc, p) => acc + pivot[p][w], 0);
                return (
                  <Td key={`total-${w}`} align="right">
                    <span className="font-medium">
                      {v > 0 ? v.toLocaleString() : <span className="text-ink-muted font-normal">—</span>}
                    </span>
                  </Td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ----- Shared cells -----

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'right' | 'center';
}) {
  return (
    <th
      className={`px-12 py-8 font-medium ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'right' | 'center';
}) {
  return (
    <td
      className={`px-12 py-8 ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
      }`}
    >
      {children}
    </td>
  );
}
